import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import {
  PROFESSIONAL_TYPES,
  canTransitionVerification,
  requiredIdentifiers,
  type ProfessionalType,
  type VerificationStatus,
} from "@titleverify/shared";
import { apiErrorText, apiFetch, ApiError, type Case, type ProfessionalProfile } from "../api.js";
import { useAuth } from "../auth.js";
import { LoadFailure } from "../components/LoadFailure.js";
import { CaseDocuments } from "../components/CaseDocuments.js";
import { parcelHeadline } from "../components/ParcelIdentity.js";
import { CaseProgressActions } from "../components/CaseProgressActions.js";

/**
 * The professional's own profile — the only way into the verification queue.
 *
 * This page shows one person their own row and nothing else. It is deliberately
 * not a peer list: the server grants `professional_profile:read` for your own
 * profile and reserves `professional_profile:list` for admins, because a list of
 * named professionals is a directory, and a directory is what COMPLIANCE
 * Constraint 1 keeps us out of.
 *
 * Every status word on this page comes from the server. Nothing here derives a
 * verification, softens one, or shows a hopeful state while a decision is
 * pending — a professional must never read this screen and conclude they are
 * verified when no admin has said so (COMPLIANCE Constraint 5).
 */

/**
 * What each status MEANS, in the terms that matter to the person reading it:
 * whether they can be given work. `assignable` is computed by the server and
 * sent on the profile; these strings only explain it.
 */
const STATUS_MEANING: Record<VerificationStatus, string> = {
  unverified:
    "Nothing has been checked. You are assignable to no case at all. Fill in your enrolment " +
    "details below and submit them for review.",
  pending:
    "Your details are with an admin for checking. You are still assignable to no case — being " +
    "in the queue is not a verification. Details cannot be edited while a review is open.",
  verified:
    "An admin has checked your enrolment against a named source and recorded what they " +
    "checked. You can be assigned to cases in your registry jurisdiction.",
  rejected:
    "An admin checked and could not confirm your enrolment. You are assignable to no case. " +
    "You can correct your details and submit again.",
  revoked:
    "A verification you previously held has been withdrawn. You are assignable to no case. " +
    "You can correct your details and submit again.",
};

export function Professional() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const { data, error, isPending } = useQuery({
    queryKey: ["professional", "me"],
    queryFn: async () => apiFetch<ProfessionalProfile>("/professionals/me", { token: await getToken() }),
    retry: false,
  });

  if (isPending) return <p className="muted" role="status">Loading your profile…</p>;

  if (error) {
    // 404 means this account has no professional profile — almost always a
    // client account that reached the URL. Say which, rather than "not found".
    if (error instanceof ApiError && error.status === 404) {
      return (
        <section>
          <p className="breadcrumb">
            <Link href="/">← Your account</Link>
          </p>
          <header className="page-head">
            <p className="eyebrow">Your standing</p>
            <h2>Your professional profile</h2>
          </header>
          {/*
            Neutral, not an alert. Having no professional profile is an ordinary
            fact about this account, not something that has gone wrong.
          */}
          <p className="notice">
            This account does not have a professional profile. Profiles are created when an account
            is opened as an Attorney-at-Law or Notary.
          </p>
        </section>
      );
    }
    /*
     * Through apiErrorText like every other failure on this page, so a refusal
     * or an unreachable service does not arrive as the bare token "forbidden".
     * The 404 is handled above precisely because it is the one status here that
     * DOES have a specific, sayable meaning.
     */
    return (
      <LoadFailure error={error} fallback="Could not load your profile.">
        <p>
          <small>
            Your standing on this platform is unchanged by this. Nothing about your
            verification depends on whether this page loaded.
          </small>
        </p>
      </LoadFailure>
    );
  }

  return (
    <>
      {/*
        The way back. This page was reachable from the masthead and from the
        account page but offered no route off itself, which on a narrow screen —
        where the masthead nav wraps below the fold — is a dead end.
      */}
      <p className="breadcrumb">
        <Link href="/">← Your account</Link>
      </p>
      <ProfileEditor
        profile={data}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ["professional", "me"] })}
      />
      <AssignedCases />
    </>
  );
}

/**
 * The cases this professional is on. Read-only, and it is the whole lawyer-facing
 * case surface for now.
 *
 * The scoping is the server's: `listCasesForActor` gives a professional the cases
 * they are assigned to and nothing else, mirroring the `case:read` rules rather
 * than re-deriving them. There is no browse, no queue of unassigned work, and no
 * sight of another professional's cases.
 *
 * Note what is NOT here: reports filed about them. A professional cannot read
 * those, which is a deliberate product decision recorded at `client_report:list`
 * in the API's authorize.ts, with its cost stated in full — somebody may lose
 * work over things said about them that they never saw in writing. The mitigation
 * is that handling is by telephone and that call is a real obligation of the
 * process, not anything in this code. The reasoning is that a written
 * back-and-forth inside the platform would make the platform the forum in which
 * an allegation is contested, and a forum that hears both sides is a body that
 * has to decide — which is exactly what we must never become.
 */
function AssignedCases() {
  const { getToken } = useAuth();

  /*
    WHICH ROWS ARE OPEN LIVES HERE, NOT IN THE ROW.
    -----------------------------------------------------------------------
    It was per-row state, and the panel was therefore rendered where the row
    is: in a <td> inside the `.table-wrap`, which scrolls horizontally. That
    put the upload form, its instructions and — worst of it — its error
    messages inside a sideways-scrolling box, so on a 375px phone a person had
    to scroll a table sideways to read why their deed scan was refused.

    A `.table-wrap` is for tabular data that legitimately outgrows a phone. A
    form is not that, and no amount of styling inside the cell fixes it while
    the panel is a descendant of the scroll container. So the state is lifted
    and the panels are rendered as siblings AFTER the table, in normal page
    flow where they wrap like everything else. The row keeps only its toggle
    button, which is small enough to belong in a cell.
  */
  const [open, setOpen] = useState<string[]>([]);

  function toggle(caseId: string) {
    setOpen((current) =>
      current.includes(caseId) ? current.filter((id) => id !== caseId) : [...current, caseId],
    );
  }

  const { data, error, isPending } = useQuery({
    queryKey: ["cases", "assigned"],
    queryFn: async () => apiFetch<{ cases: Case[] }>("/cases", { token: await getToken() }),
  });

  if (isPending) return <p className="muted" role="status">Loading your cases…</p>;
  if (error) return <LoadFailure error={error} fallback="Could not load your cases." />;

  return (
    <section>
      <header className="page-head">
        <p className="eyebrow">Assigned to you</p>
        <h2>Your cases</h2>
      </header>
      {data.cases.length === 0 ? (
        <p className="empty">
          You are not assigned to any case. That is the whole of what this says — it is not a
          judgement about you, and a case can only reach you once an admin assigns it.
        </p>
      ) : (
        <>
          <div className="table-wrap" tabIndex={0} role="region" aria-label="Cases assigned to you">
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  {/*
                    The attorney is told to search a registry for a parcel; until
                    now this screen never said WHICH parcel. The district, village
                    and plan the client typed were on the server the whole time
                    and were shown to nobody.
                  */}
                  <th>The land</th>
                  <th>Status</th>
                  <th>Registry district</th>
                  <th>Land system</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {data.cases.map((c) => (
                  <AssignedCaseRow
                    key={c.id}
                    caseRow={c}
                    open={open.includes(c.id)}
                    onToggle={() => toggle(c.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/*
            The opened panels, outside the scroll container. Each one names the
            case it belongs to, which the old in-row placement got for free and
            this does not — a panel detached from its row must say which case's
            papers it is showing, or the professional is looking at documents
            with no idea whose they are. That is a fact about a case, so it is
            printed rather than left to be inferred from position.
          */}
          {data.cases
            .filter((c) => open.includes(c.id))
            .map((c) => (
              <article className="card" key={c.id} id={panelId(c.id)}>
                <header className="card-head">
                  <h3 className="card-title">
                    Case <code>{c.id}</code>
                  </h3>
                </header>

                <CaseDocuments caseId={c.id} viewer="professional" />

                <CaseProgressActions caseRow={c} />

                {/*
                  A way out that does not require scrolling back up past a whole
                  upload form to the row this was opened from.
                */}
                <div className="actions">
                  <button type="button" className="quiet" onClick={() => toggle(c.id)}>
                    Hide these documents
                  </button>
                </div>
              </article>
            ))}
        </>
      )}
    </section>
  );
}

/** The panel a row's button controls, so `aria-controls` can point at it. */
function panelId(caseId: string): string {
  return `case-documents-${caseId}`;
}

/**
 * One assigned case. The row is only the row: it holds the button that opens the
 * custody panel, and the panel itself is rendered by AssignedCases outside the
 * scrolling table — see the note there for why.
 *
 * BEHIND A DISCLOSURE rather than always open, unlike the client's card. A
 * professional can hold many cases and each panel is its own request for its own
 * case's documents; mounting forty of them to render one table would be a lot of
 * traffic for a question nobody asked. The client's list is their own handful of
 * cases and the promise to upload was made to them, so that one is always shown.
 */
function AssignedCaseRow({
  caseRow,
  open,
  onToggle,
}: {
  caseRow: Case;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <tr>
      <td>
        {/*
          The case detail screen is scoped by the server, not by this link: an
          attorney reaches it because they are the assigned professional on this
          row, which is the same fact that put the case in this list.
        */}
        <Link href={`/cases/${caseRow.id}`}>
          <code>{caseRow.id}</code>
        </Link>
      </td>
      {/* Client-supplied and unverified — see ParcelIdentity. */}
      <td>
        {caseRow.parcel && parcelHeadline(caseRow.parcel) ? (
          parcelHeadline(caseRow.parcel)
        ) : (
          <span className="muted">not described yet</span>
        )}
      </td>
      {/*
        A workflow state gets .state, never the verification palette: the
        platform has no view on whether a case sitting at one step rather than
        another is good news.
      */}
      <td>
        <span className="state">{caseRow.status}</span>
      </td>
      {/* Null is "not resolved yet", and says so. Never a blank that reads as an answer. */}
      <td>{caseRow.registryDistrict ?? <span className="muted">not yet resolved</span>}</td>
      <td>{caseRow.landSystem ?? <span className="muted">not yet established</span>}</td>
      <td>
        {/*
          The label says nothing about whether the case HAS documents, because
          this row does not know — the count would be a second request per case,
          and a guess would be a fabricated fact about the case.

          The panel it opens is not next to this button — it is below the table —
          so `aria-controls` names it, and the row says where it went rather than
          leaving someone to hunt for it.
        */}
        <button
          type="button"
          className="quiet compact"
          aria-expanded={open}
          aria-controls={panelId(caseRow.id)}
          onClick={onToggle}
        >
          {open ? "Hide documents" : "Show documents"}
        </button>
        {open ? <p className="muted">Shown below this table.</p> : null}
      </td>
    </tr>
  );
}

function ProfileEditor({
  profile,
  onChanged,
}: {
  profile: ProfessionalProfile;
  onChanged: () => void;
}) {
  const { getToken } = useAuth();
  const [type, setType] = useState<ProfessionalType>(profile.type);
  const [enrolmentNo, setEnrolmentNo] = useState(profile.enrolmentNo ?? "");
  const [notaryRegistrationNo, setNotaryRegistrationNo] = useState(
    profile.notaryRegistrationNo ?? "",
  );

  const save = useMutation({
    mutationFn: async () =>
      apiFetch<ProfessionalProfile>("/professionals/me", {
        token: await getToken(),
        method: "PATCH",
        body: {
          type,
          // Empty means "not provided", which is null — not an empty string that
          // would satisfy a presence check while proving nothing.
          enrolmentNo: enrolmentNo.trim() === "" ? null : enrolmentNo.trim(),
          notaryRegistrationNo:
            notaryRegistrationNo.trim() === "" ? null : notaryRegistrationNo.trim(),
        },
      }),
    onSuccess: onChanged,
  });

  const submit = useMutation({
    mutationFn: async () =>
      apiFetch<ProfessionalProfile>("/professionals/me/submit", {
        token: await getToken(),
        method: "POST",
      }),
    onSuccess: onChanged,
  });

  // Editing is refused by the server once verified or once a review is open. The
  // UI mirrors that rather than enforcing it — the server is what refuses.
  const locked = profile.verificationStatus === "verified" || profile.verificationStatus === "pending";

  // Which identifiers this claimed type requires comes from the shared rule the
  // server uses, not a copy of it — an attorney is enrolled by the Supreme
  // Court, a notary is commissioned under the Notaries Ordinance, and someone
  // who is both must prove both.
  const required = requiredIdentifiers(profile.type);
  const stored = { enrolmentNo: profile.enrolmentNo, notaryRegistrationNo: profile.notaryRegistrationNo };
  const missing = required.filter((field) => !stored[field]?.trim());

  // Two independent reasons submission can be unavailable, and the user is told
  // which one applies rather than shown a dead button.
  const transitionAllowed = canTransitionVerification(profile.verificationStatus, "pending");
  const blockedReason = !transitionAllowed
    ? profile.verificationStatus === "pending"
      ? "Already submitted — an admin has not decided yet."
      : "A verified profile cannot be re-submitted. An admin must revoke the verification first."
    : missing.length > 0
      ? `Save these first: ${missing.map(labelFor).join(", ")}.`
      : null;

  return (
    <section>
      <header className="page-head">
        <p className="eyebrow">Your standing</p>
        <h2>Your professional profile</h2>
      </header>

      <dl>
        <dt>Verification status</dt>
        <dd>
          {/*
            The server's word, as a token. Colour accompanies it and never
            replaces it — a reader who cannot tell the hues apart still reads
            whether their enrolment has been proven.
          */}
          <span className={`status status-${profile.verificationStatus}`}>
            {profile.verificationStatus}
          </span>
        </dd>
        <dt>What that means</dt>
        <dd>{STATUS_MEANING[profile.verificationStatus]}</dd>
        <dt>Assignable to cases</dt>
        {/* The server sends this; it is not re-derived here. */}
        <dd>{profile.assignable ? "Yes" : "No"}</dd>
      </dl>

      {/*
        REVOKED, HONESTLY.
        ===================================================================
        Two things have to be said here and neither may be softened.

        FIRST, what it does not mean. Revocation ends this platform's own
        willingness to route work; it has no bearing whatever on enrolment as an
        attorney-at-law, which is the Supreme Court's and not ours. Anything that
        let a professional read this screen as a sanction would be the platform
        implying a finding it has not made and may not make.

        SECOND, the reason — and here the honest answer is that WE CANNOT SHOW
        IT. The admin picks a reason code, and it is recorded, but it is recorded
        in the audit log only: professional_profiles has no revocation-reason
        column (see the schema), and the audit log is not a professional-facing
        surface. So this says the reason exists and is written down, and that an
        admin can tell them what it was. It does not guess at one. A plausible
        invented reason next to a real decision is the exact fabrication CLAUDE.md
        non-negotiable 1 forbids, and it would be a fabrication about a person.
      */}
      {profile.verificationStatus === "revoked" ? (
        <div role="alert">
          <p>
            <strong>Your verification on this platform has been withdrawn.</strong> This is a
            decision about whether TitleVerify routes cases to you. It is not a finding that you
            did anything wrong, it is not a sanction, and it has no effect at all on your
            enrolment as an Attorney-at-Law — that is a matter for the Bar Association of Sri
            Lanka and the Supreme Court, and nothing here touches it.
          </p>
          <p>
            An administrator recorded a reason code when they made this decision.{" "}
            <strong>This page cannot show you which one</strong> — the reason is held in the
            platform's audit record rather than on your profile, and we are not going to guess at
            it here. Ask an administrator and they can tell you what was recorded and why.
          </p>
        </div>
      ) : null}

      {locked ? (
        // True and worth knowing, and not a problem: neutral notice, never the
        // danger colours. Being locked because you are verified is good news.
        <p className="notice">
          {profile.verificationStatus === "verified"
            ? "These details are locked. An admin proved a specific enrolment number against a " +
              "specific source, and that record has to keep pointing at the thing they checked. " +
              "Changing it here would leave the proof describing details that no longer exist. " +
              "If something is genuinely wrong, ask an admin to revoke the verification — that " +
              "decision is recorded — and then submit the corrected details."
            : "These details are locked while an admin is reviewing them."}
        </p>
      ) : null}

      {save.error ? <p role="alert">{apiErrorText(save.error, "Could not save your details.")}</p> : null}

      <fieldset>
        <legend>Your enrolment details</legend>

        <label htmlFor="type">I am</label>
        <select
          id="type"
          value={type}
          disabled={locked}
          onChange={(e) => setType(e.target.value as ProfessionalType)}
        >
          {PROFESSIONAL_TYPES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <p>
          <small>
            Claimed, not proven. An admin decides what you actually are at verification.{" "}
            {requiredIdentifiers(type).length === 1
              ? `A ${type} must provide a ${labelFor(requiredIdentifiers(type)[0]!)}.`
              : "Someone who is both must provide both identifiers below."}
          </small>
        </p>

        <label htmlFor="enrolmentNo">Enrolment no. (Supreme Court)</label>
        <input
          id="enrolmentNo"
          value={enrolmentNo}
          disabled={locked}
          onChange={(e) => setEnrolmentNo(e.target.value)}
        />

        <label htmlFor="notaryRegistrationNo">Notary registration no.</label>
        <input
          id="notaryRegistrationNo"
          value={notaryRegistrationNo}
          disabled={locked}
          onChange={(e) => setNotaryRegistrationNo(e.target.value)}
        />

        <div className="actions">
          <button type="button" disabled={locked || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save details"}
          </button>
        </div>
      </fieldset>

      <hr />

      <h3>Submit for verification</h3>
      {/*
        What submitting does, said before it is done — and in particular what it
        does NOT do. Nobody may read this screen and conclude they are verified
        because they pressed a button (COMPLIANCE Constraint 5).
      */}
      <div className="consequence">
        <p>
          This puts your details in front of an admin. It does not verify you, and you stay
          assignable to nothing until an admin records what they checked and how.
        </p>

        {submit.error ? (
          <p role="alert">{apiErrorText(submit.error, "Could not submit your details for verification.")}</p>
        ) : null}

        {/* The reason is stated, not implied by a greyed-out control. */}
        {blockedReason ? <p className="notice">{blockedReason}</p> : null}

        <div className="actions">
          <button
            type="button"
            disabled={blockedReason !== null || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? "Submitting…" : "Submit for verification"}
          </button>
        </div>
      </div>
    </section>
  );
}

function labelFor(field: "enrolmentNo" | "notaryRegistrationNo"): string {
  return field === "enrolmentNo"
    ? "enrolment no. (Supreme Court)"
    : "notary registration no.";
}
